import React, { useRef, useState, useEffect } from 'react'
import ArrowButton from './ArrowButtonSmall'


const ARROW_BTN__LARGE_SIZE = 40
const ARROW_BTN__MEDIUM_SIZE = 28
const ARROW_BTN__SMALL_SIZE = 20
const BTN_MARGIN = 6
const SCROLLER_PADDING = 12
const FADE_START_OFFSET = 24

interface HorizontalScrollerProps {
  width?: number
  children: React.ReactNode
  showScrollArrows?: boolean
  arrowPlacement?: 'inside' | 'outside' | 'edge'
  /** 'hover' (default) reveals arrows only while hovering the row; 'always' keeps them
   *  visible whenever the row is scrollable (HTML behavior). */
  arrowVisibility?: 'hover' | 'always'
  /** Notified whenever arrow (scrollable) state changes - e.g. to add spacing next to
   *  the row only while an arrow is showing. */
  onArrowStateChange?: (state: { left: boolean; right: boolean }) => void
  arrowVerticalPosition?: number | 'center'
  contentHeight?: number
  /** Fixed row height every item stretches to, so cards share one baseline and can
   *  derive their width from an aspect ratio. Omit to size the row by its content. */
  itemHeight?: number
  arrowButtonSize?: 'large' | 'medium' | 'small'
  /** Glyph inside the arrow buttons: 'chevron' (default) or HTML-style 'line' arrow. */
  arrowIconVariant?: 'chevron' | 'line'
  scrollToStartTrigger?: string | number
  gap?: number
  contentAlign?: 'center' | 'start'
  /** Fade the row edges under the arrows. Set false for the HTML-style look where
   *  the row is simply padded away from the arrow instead of faded. Default true. */
  showFadeMask?: boolean
  /** Scroll by ~one viewport per arrow click (HTML behavior) instead of snapping to
   *  the next partially-hidden item. Default false. */
  pageScroll?: boolean
  /** Small overshoot-and-settle when a page scroll lands on the first/last item, as a
   *  tactile "you've reached the end" cue. Only applies with pageScroll. Default false. */
  edgeBounce?: boolean
  /** Only applies to arrowPlacement 'edge'. 'overlap' (default) matches the HTML mock/Figma
   *  component exactly - the button's outer half hangs outside the row, its inner half sits
   *  over the row's last visible content. 'full' reserves a gap equal to half the button's
   *  width instead, so the button clears the content entirely with no overlap. */
  edgeClearance?: 'overlap' | 'full'
}

function HorizontalScroller({
  width,
  children,
  showScrollArrows = true,
  arrowPlacement = 'inside',
  arrowVisibility = 'hover',
  onArrowStateChange,
  arrowButtonSize = 'medium',
  arrowIconVariant = 'chevron',
  arrowVerticalPosition = 16,
  itemHeight,
  gap = 6,
  contentAlign = 'center',
  showFadeMask = true,
  pageScroll = false,
  edgeBounce = false,
  edgeClearance = 'overlap',
  scrollToStartTrigger: resetTrigger
}: HorizontalScrollerProps) {
  const scrollContainerRef = useRef<HTMLDivElement>(null)
  const mainContainerRef = useRef<HTMLDivElement>(null)
  const leftArrowRef = useRef<HTMLDivElement>(null)
  const rightArrowRef = useRef<HTMLDivElement>(null)
  const [showLeftArrow, setShowLeftArrow] = useState(false)
  const [showRightArrow, setShowRightArrow] = useState(false)
  const [isHovering, setIsHovering] = useState(false)
  const [hasOverflow, setHasOverflow] = useState(false)

  const leaveTimeoutRef = useRef<NodeJS.Timeout | null>(null)

  const ARROW_BTN_SIZE =
    arrowButtonSize === 'large'
      ? ARROW_BTN__LARGE_SIZE
      : arrowButtonSize === 'medium'
      ? ARROW_BTN__MEDIUM_SIZE
      : ARROW_BTN__SMALL_SIZE

  const padding = ARROW_BTN_SIZE + BTN_MARGIN
  // Small breathing room between content and a showing arrow when the fade mask is
  // disabled. Flat 12px (matches the HTML) - not arrow-size based, which over-inset the
  // row so it ended near the arrow's center rather than under its outer half. 'full'
  // clearance opts into that size-based inset instead, so the button clears content fully.
  const edgeArrowPad = edgeClearance === 'full' ? Math.round(ARROW_BTN_SIZE / 2) : SCROLLER_PADDING
  const fadeStart = ARROW_BTN_SIZE + FADE_START_OFFSET + (arrowPlacement === 'inside' && isHovering && showScrollArrows ? padding / 2  : 0)
  const fadeEnd = SCROLLER_PADDING + (arrowPlacement === 'inside' && isHovering && showScrollArrows ? padding / 2 : 0)

  const getArrowTop = () => {
    if (arrowVerticalPosition === 'center') return '50%'
    return `${arrowVerticalPosition}px`
  }

  const arrowVerticalTransform = arrowVerticalPosition === 'center' ? 'translateY(-50%)' : undefined

  const getArrowLeftPosition = () => {
    if (arrowPlacement === 'outside') return '0'
    // 'edge': centered ON the row edge (half hanging into the side padding), like the HTML.
    if (arrowPlacement === 'edge') return `-${Math.round(ARROW_BTN_SIZE / 2)}px`
    return `${SCROLLER_PADDING - Math.round(ARROW_BTN_SIZE / 2)}px`
  }

  const getArrowRightPosition = () => {
    if (arrowPlacement === 'outside') return '0'
    if (arrowPlacement === 'edge') return `-${Math.round(ARROW_BTN_SIZE / 2)}px`
    return `${SCROLLER_PADDING - Math.round(ARROW_BTN_SIZE / 2)}px`
  }

  const checkScrollPosition = () => {
    if (!scrollContainerRef.current) return

    const { scrollLeft, scrollWidth, clientWidth } = scrollContainerRef.current
    const overflow = scrollWidth > clientWidth + 1
    const left = scrollLeft > 0
    const right = scrollLeft < scrollWidth - clientWidth - 1
    setHasOverflow(overflow)
    setShowLeftArrow(left)
    setShowRightArrow(right)
    onArrowStateChange?.({ left, right })
  }

  useEffect(() => {
    checkScrollPosition()
  }, [])

  // Recompute overflow/fade on window resize too. The ResizeObserver alone can miss
  // width changes of a flex child that only start overflowing after the window grows,
  // leaving the fade/arrows stale. A settle pass covers reflow that lags the resize.
  useEffect(() => {
    let settle: ReturnType<typeof setTimeout>
    const onResize = () => {
      checkScrollPosition()
      clearTimeout(settle)
      settle = setTimeout(checkScrollPosition, 250)
    }
    window.addEventListener('resize', onResize)
    return () => {
      window.removeEventListener('resize', onResize)
      clearTimeout(settle)
    }
  }, [])

  // Re-check when the children change (e.g. switching template category swaps the items).
  useEffect(() => {
    checkScrollPosition()
  }, [children])

  // Check if scrollable content has loaded after any descendant <img> finishes loading
  useEffect(() => {
    const container = scrollContainerRef.current
    if (!container) return
    const onImageLoad = () => checkScrollPosition()
    container.addEventListener('load', onImageLoad, true)
    return () => container.removeEventListener('load', onImageLoad, true)
  }, [])

  useEffect(() => {
    return () => {
      if (leaveTimeoutRef.current) {
        clearTimeout(leaveTimeoutRef.current)
      }
    }
  }, [])

  useEffect(() => {
    if (!showScrollArrows) return

    const syncHoverState = () => {
      const nextIsHovering = Boolean(
        mainContainerRef.current?.matches(':hover') ||
        leftArrowRef.current?.matches(':hover') ||
        rightArrowRef.current?.matches(':hover')
      )

      setIsHovering(prev => (prev === nextIsHovering ? prev : nextIsHovering))
    }
    const timeoutId = setTimeout(syncHoverState, 100)
    syncHoverState()

    return () => {
      clearTimeout(timeoutId)
    }
  }, [])
  
  useEffect(() => {
    if (!scrollContainerRef.current) return
    scrollContainerRef.current.scrollTo({ left: 0, behavior: 'auto' })
    checkScrollPosition()
  }, [resetTrigger])

  const scrollToNextPartiallyHidden = (direction: 'left' | 'right') => {
    if (!scrollContainerRef.current) return

    const container = scrollContainerRef.current
    const containerRect = container.getBoundingClientRect()
    const children = Array.from(container.children) as HTMLElement[]

    // Clamp the target so smooth-scroll never overshoots past the start/end and
    // "bounces" back once the browser re-clamps it to the valid scroll range.
    const maxScroll = container.scrollWidth - container.clientWidth
    const clamp = (value: number) => Math.max(0, Math.min(value, maxScroll))

    // Page-scroll mode (HTML behavior): jump ~one viewport per click and let the
    // native smooth scroll decelerate into the edge, optionally with a small bounce.
    if (pageScroll) {
      const delta = Math.round(container.clientWidth * 0.85)
      const raw = direction === 'right' ? container.scrollLeft + delta : container.scrollLeft - delta
      const target = clamp(raw)
      const hitsEdge = target === 0 || target === maxScroll
      if (edgeBounce && hitsEdge) {
        const overshoot = target === 0 ? -16 : maxScroll + 16
        container.scrollTo({ left: overshoot, behavior: 'smooth' })
        setTimeout(() => container.scrollTo({ left: target, behavior: 'smooth' }), 140)
      } else {
        container.scrollTo({ left: target, behavior: 'smooth' })
      }
      setTimeout(checkScrollPosition, 300)
      return
    }

    if (direction === 'right') {
      // Find the first partially hidden or fully hidden element on the right
      const targetElement = children.find(child => {
        const childRect = child.getBoundingClientRect()
        return childRect.right > containerRect.right - SCROLLER_PADDING
      })

      if (targetElement) {
        const targetRect = targetElement.getBoundingClientRect()
        const scrollAmount = targetRect.left - containerRect.left + container.scrollLeft - SCROLLER_PADDING
        container.scrollTo({ left: clamp(scrollAmount), behavior: 'smooth' })
      }
    } else {
      // Find the first partially hidden or fully hidden element on the left
      const targetElement = children.reverse().find(child => {
        const childRect = child.getBoundingClientRect()
        return childRect.left < containerRect.left
      })

      if (targetElement) {
        const targetRect = targetElement.getBoundingClientRect()
        const scrollAmount = targetRect.right - containerRect.right + container.scrollLeft
        container.scrollTo({ left: clamp(scrollAmount), behavior: 'smooth' })
      }
    }

    // Update arrow visibility after scroll completes
    setTimeout(checkScrollPosition, 300)
  }

  const handleMouseLeave = (e: React.MouseEvent) => {
    const relatedTarget = e.relatedTarget

    // Check if we're moving to one of our own elements
    if (relatedTarget instanceof Node && (
      mainContainerRef.current?.contains(relatedTarget) ||
      leftArrowRef.current?.contains(relatedTarget) ||
      rightArrowRef.current?.contains(relatedTarget)
    )) {
      return // Don't hide if moving within our component
    }
    
    // Actually leaving the component
    leaveTimeoutRef.current = setTimeout(() => {
      setIsHovering(false)
    }, 100)
  }

  const handleMouseEnter = () => {
    if (leaveTimeoutRef.current) {
      clearTimeout(leaveTimeoutRef.current)
      leaveTimeoutRef.current = null
    }
    setIsHovering(true)
  }

  const containerStyle = arrowPlacement === 'outside'
    ? {
        position: 'relative' as const,
        width: width ? `${width + padding * 2}px` : 'auto',
        margin: `0 -${padding}px`,
        padding: `0 ${padding}px`
      }
    : {
        // 'inside' and 'edge' keep the wrapper flush; 'edge' arrows hang out via a
        // negative offset instead of reserving wrapper padding.
        position: 'relative' as const,
        width: width ? `${width}px` : 'auto'
      }

  const getPad = () =>
    arrowPlacement === 'outside'
      ? {
          padding: `${Math.round(ARROW_BTN_SIZE * 0.35)}px`,
          margin: `-${Math.round(ARROW_BTN_SIZE * 0.35)}px`,
        }
      : {}

  // Whether each arrow is currently shown. In 'always' mode it tracks scrollability
  // directly; in 'hover' mode it additionally requires the row to be hovered. The
  // wrapper stays mounted either way so show/hide fades instead of popping.
  const canReveal = arrowVisibility === 'always' || isHovering
  const visibleLeft = showLeftArrow && showScrollArrows && canReveal
  const visibleRight = showRightArrow && showScrollArrows && canReveal

  return (
    <div
      ref={mainContainerRef}
      style={containerStyle}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {showScrollArrows && (
        <div
          ref={leftArrowRef}
          style={{
            position: 'absolute',
            left: getArrowLeftPosition(),
            top: getArrowTop(),
            transform: arrowVerticalTransform,
            zIndex: 100,
            ...getPad(),
            opacity: visibleLeft ? 1 : 0,
            pointerEvents: visibleLeft ? 'auto' : 'none',
            transition: 'opacity 0.15s ease',
          }}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        >
          <ArrowButton
            direction="left"
            onClick={() => scrollToNextPartiallyHidden('left')}
            show
            size={ARROW_BTN_SIZE}
            iconVariant={arrowIconVariant}
          />
        </div>
      )}

      <div
        ref={scrollContainerRef}
        className="horizontal-scroller-container"
        style={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: contentAlign === 'start' ? 'flex-start' : hasOverflow ? 'flex-start' : 'center',
          // With a fixed row height, items stretch to it and `content-box` keeps that
          // height exact — the row's own vertical padding stays outside it (and is
          // cancelled by the negative margins below), so items get the full itemHeight.
          ...(itemHeight != null
            ? { height: `${itemHeight}px`, alignItems: 'stretch' as const, boxSizing: 'content-box' as const }
            : {}),
          gap: `${gap}px`,
          overflowX: 'auto',
          overflowY: 'visible',
          scrollbarWidth: 'none',
          msOverflowStyle: 'none',
          paddingTop: `${SCROLLER_PADDING}px`,
          paddingBottom: `${SCROLLER_PADDING}px`,
          // With no fade, pad the row away from a showing arrow (HTML behavior) so the
          // floating button never sits on top of content. With fade, keep flat padding.
          // 'full' clearance has no base pad (margin isn't cancelling it either) - only
          // the arrow-shown pad applies, sized to clear the button exactly.
          paddingLeft: `${(edgeClearance === 'full' ? 0 : SCROLLER_PADDING) + (!showFadeMask && showLeftArrow && showScrollArrows ? edgeArrowPad : 0)}px`,
          paddingRight: `${(edgeClearance === 'full' ? 0 : SCROLLER_PADDING) + (!showFadeMask && showRightArrow && showScrollArrows ? edgeArrowPad : 0)}px`,
          marginTop: `-${SCROLLER_PADDING}px`,
          marginBottom: `-${SCROLLER_PADDING}px`,
          // 'overlap' cancels the base padding (flush, matches mock) so only the extra
          // per-arrow pad below creates any gap. 'full' skips that cancellation, so the
          // base padding itself is a permanent minimum inset - the row never sits flush
          // with the button's center, even when the arrow happens to be hidden.
          marginLeft: edgeClearance === 'full' ? '0' : `-${SCROLLER_PADDING}px`,
          marginRight: edgeClearance === 'full' ? '0' : `-${SCROLLER_PADDING}px`,
          transition: 'padding .15s',
          maskImage: showFadeMask
            ? `linear-gradient(to right, ${showLeftArrow ? `transparent 0%, black ${fadeStart}px` : 'black 0%'}, black calc(100% - ${fadeStart}px), ${showRightArrow ? `transparent calc(100% - ${fadeEnd}px)` : 'black 100%'})`
            : undefined,
          WebkitMaskImage: showFadeMask
            ? `linear-gradient(to right, ${showLeftArrow ? `transparent 0%, black ${fadeStart}px` : 'black 0%'}, black calc(100% - ${fadeStart}px), ${showRightArrow ? `transparent calc(100% - ${fadeEnd}px)` : 'black 100%'})`
            : undefined,
        }}
        onScroll={checkScrollPosition}
      >
        {children}
        <style>
          {`
            .horizontal-scroller-container::-webkit-scrollbar {
              display: none;
            }
          `}
        </style>
      </div>

      {showScrollArrows && (
        <div
          ref={rightArrowRef}
          style={{
            position: 'absolute',
            right: getArrowRightPosition(),
            top: getArrowTop(),
            transform: arrowVerticalTransform,
            zIndex: 100,
            ...getPad(),
            opacity: visibleRight ? 1 : 0,
            pointerEvents: visibleRight ? 'auto' : 'none',
            transition: 'opacity 0.15s ease',
          }}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        >
          <ArrowButton
            direction="right"
            onClick={() => scrollToNextPartiallyHidden('right')}
            show
            size={ARROW_BTN_SIZE}
            iconVariant={arrowIconVariant}
          />
        </div>
      )}
    </div>
  )
}

export default HorizontalScroller
